import React, { useMemo, useState, useRef, useLayoutEffect } from 'react';
// import { X, RotateCcw } from 'lucide-react';
import { RotateCcw } from 'lucide-react';
import '../styles/ActiveFiltersBar.css';
import * as FeatherIcon from 'react-feather';
import { industriesMap } from '../utils/industryConstants';
import { ChevronDown, ChevronUp } from 'react-feather';


const ActiveFiltersBar = ({
    selectedCompanies = [],
    selectedNewsTypes = [],
    selectedRegions = [],
    selectedIndustryNewsDomains = [],
    selectedCustomerDomains = [],
    searchQuery = '',
    companyNames = {},
    newsTypesMap = {},
    regionsMap = {},
    domainNames = {},
    onlyWithTimeline = false,
    userIndustry = '',
    consumerIndustries = [],
    onRemoveFilter,
    onResetAll
}) => {
    const userIndustryData = userIndustry ? industriesMap[userIndustry] : null;

    const filteredCustomerIndustries = useMemo(() => {
        if (!consumerIndustries || consumerIndustries.length === 0) return {};
        const filtered = {};
        consumerIndustries.forEach(name => {
            if (industriesMap[name] && name !== userIndustry) {
                filtered[name] = industriesMap[name];
            }
        });
        return filtered;
    }, [consumerIndustries, userIndustry]);

    // Industry news chip count: 1 if parent fully selected, else individual count
    const industryNewsChipCount = useMemo(() => {
        if (!userIndustryData || selectedIndustryNewsDomains.length === 0) return 0;
        const allDomains = Object.values(userIndustryData.domains);
        return allDomains.every(v => selectedIndustryNewsDomains.includes(v)) ? 1 : selectedIndustryNewsDomains.length;
    }, [userIndustryData, selectedIndustryNewsDomains]);

    // Customer industry chip count: 1 per fully-selected parent, else individual
    const customerDomainsChipCount = useMemo(() => {
        return Object.values(filteredCustomerIndustries).reduce((total, industryData) => {
            const allDomains = Object.values(industryData.domains);
            const selectedForThis = allDomains.filter(v => selectedCustomerDomains.includes(v));
            if (selectedForThis.length === 0) return total;
            return total + (allDomains.every(v => selectedCustomerDomains.includes(v)) ? 1 : selectedForThis.length);
        }, 0);
    }, [filteredCustomerIndustries, selectedCustomerDomains]);

    const [isExpanded, setIsExpanded] = useState(false);
    const [hasOverflow, setHasOverflow] = useState(false);
    const [rowHeight, setRowHeight] = useState(0);
    const chipsRef = useRef(null);
    // OLD: const COLLAPSED_HEIGHT = 38;

    // Calculate total active filters
    const totalFilters = selectedCompanies.length +
        selectedNewsTypes.length +
        selectedRegions.length +
        industryNewsChipCount +
        customerDomainsChipCount +
        (searchQuery ? 1 : 0) +
        (onlyWithTimeline ? 1 : 0);

    // Position-based overflow detection — more reliable than scrollHeight comparison
    // OLD: useEffect(() => {
    //     if (chipsRef.current) {
    //         setHasOverflow(chipsRef.current.scrollHeight > COLLAPSED_HEIGHT);
    //     }
    // }, [totalFilters]);
    useLayoutEffect(() => {
        if (!chipsRef.current) return;
        const chips = Array.from(chipsRef.current.children);
        if (chips.length === 0) {
            setHasOverflow(false);
            setRowHeight(0);
            return;
        }
        const containerTop = chipsRef.current.getBoundingClientRect().top;
        const firstChip = chips[0].getBoundingClientRect();
        const oneRowHeight = Math.ceil(firstChip.bottom - containerTop);
        setRowHeight(oneRowHeight);
        const firstTop = firstChip.top;
        const isMultiRow = chips.some(chip => chip.getBoundingClientRect().top > firstTop + 3);
        setHasOverflow(isMultiRow);
    }, [totalFilters]);

    // Don't render if no filters are active
    if (totalFilters === 0) {
        return null;
    }

    // Render industry news chips (grouped by parent)
    const renderIndustryNewsChips = () => {
        if (!userIndustryData || selectedIndustryNewsDomains.length === 0) return null;
        const allDomainValues = Object.values(userIndustryData.domains);
        const allSelected = allDomainValues.every(v => selectedIndustryNewsDomains.includes(v));

        if (allSelected) {
            return (
                <div key="industryNewsParent" className="filter-chip-news filter-chip-newstype-news">
                    <span className="chip-label-news">Industry News:</span>
                    <span className="chip-value-news">{userIndustry}</span>
                    <button
                        className="chip-remove-news"
                        onClick={() => allDomainValues.forEach(v => onRemoveFilter('industryNewsTrend', v))}
                        aria-label={`Remove ${userIndustry} industry news filter`}
                    >
                        <FeatherIcon.XCircle className='icons_table ' />
                    </button>
                </div>
            );
        }

        return selectedIndustryNewsDomains.map(domainValue => {
            const subName = Object.entries(userIndustryData.domains).find(([, v]) => v === domainValue)?.[0]
                || domainNames[domainValue] || domainValue;
            return (
                <div key={`industrynews-${domainValue}`} className="filter-chip-news filter-chip-newstype-news">
                    <span className="chip-label-news">Industry News:</span>
                    <span className="chip-value-news">{userIndustry} ({subName})</span>
                    <button
                        className="chip-remove-news"
                        onClick={() => onRemoveFilter('industryNewsTrend', domainValue)}
                        aria-label={`Remove ${subName} filter`}
                    >
                        <FeatherIcon.XCircle className='icons_table ' />
                    </button>
                </div>
            );
        });
    };

    // Render customer industry chips (grouped by parent)
    const renderCustomerIndustryChips = () => {
        return Object.entries(filteredCustomerIndustries).map(([industryName, industryData]) => {
            const allDomainValues = Object.values(industryData.domains);
            const selectedForThis = allDomainValues.filter(v => selectedCustomerDomains.includes(v));
            if (selectedForThis.length === 0) return null;

            const allSelected = allDomainValues.every(v => selectedCustomerDomains.includes(v));

            if (allSelected) {
                return (
                    <div key={`customerParent-${industryData.code}`} className="filter-chip-news filter-chip-region-news">
                        <span className="chip-label-news">Customer Industry:</span>
                        <span className="chip-value-news">{industryName}</span>
                        <button
                            className="chip-remove-news"
                            onClick={() => allDomainValues.forEach(v => onRemoveFilter('industryTrend', v))}
                            aria-label={`Remove ${industryName} customer industry filter`}
                        >
                            <FeatherIcon.XCircle className='icons_table ' />
                        </button>
                    </div>
                );
            }

            return selectedForThis.map(domainValue => {
                const subName = Object.entries(industryData.domains).find(([, v]) => v === domainValue)?.[0]
                    || domainNames[domainValue] || domainValue;
                return (
                    <div key={`customerind-${domainValue}`} className="filter-chip-news filter-chip-region-news">
                        <span className="chip-label-news">Customer Industry:</span>
                        <span className="chip-value-news">{industryName} ({subName})</span>
                        <button
                            className="chip-remove-news"
                            onClick={() => onRemoveFilter('industryTrend', domainValue)}
                            aria-label={`Remove ${subName} filter`}
                        >
                            <FeatherIcon.XCircle className='icons_table ' />
                        </button>
                    </div>
                );
            });
        });
    };

    return (
        <div className="active-filters-bar-news">
            <div className="active-filters-header-news">
                <span className="active-filters-title-news">
                    Active Filters <span className="filter-count-news">({totalFilters})</span>
                </span>
                <div className="filter-header-actions-news">
                    {/* {hasOverflow && (
                        <button
                            className="show-more-filters-news"
                            onClick={() => setIsExpanded(prev => !prev)}
                        >
                            {isExpanded ? 'Show less ▲' : 'Show more ▼'}
                        </button>
                    )} */}
                    {hasOverflow && (
                        <button
                            className="show-more-filters-news"
                            onClick={() => setIsExpanded(prev => !prev)}
                        >
                            {isExpanded ? (
                                <>
                                    Show less <ChevronUp className="" size={16} />
                                </>
                            ) : (
                                <>
                                    Show more <ChevronDown className="" size={16} />
                                </>
                            )}
                        </button>
                    )}
                    <button className="reset-all-button-news" onClick={onResetAll}>
                        <RotateCcw size={14} />
                        Reset All
                    </button>
                </div>
            </div>

            {/* OLD: <div className={`filter-chips-container-news${!isExpanded ? ' chips-collapsed-news' : ''}`}> */}
            <div
                ref={chipsRef}
                className="filter-chips-container-news"
                style={!isExpanded && rowHeight > 0 ? { maxHeight: `${rowHeight}px`, overflow: 'hidden' } : {}}
            >
                {/* Search Query Chip */}
                {searchQuery && (
                    <div className="filter-chip-news filter-chip-search-news">
                        <span className="chip-label-news">Search:</span>
                        <span className="chip-value-news">{searchQuery}</span>
                        <button
                            className="chip-remove-news"
                            onClick={() => onRemoveFilter('search', searchQuery)}
                            aria-label="Remove search filter"
                        >
                            <FeatherIcon.XCircle className='icons_table ' />
                        </button>
                    </div>
                )}

                {/* Company Chips */}
                {selectedCompanies.map(companyCode => (
                    <div key={`company-${companyCode}`} className="filter-chip-news filter-chip-company-news">
                        <span className="chip-label-news">Company:</span>
                        <span className="chip-value-news">{companyNames[companyCode] || companyCode}</span>
                        <button
                            className="chip-remove-news"
                            onClick={() => onRemoveFilter('company', companyCode)}
                            aria-label={`Remove ${companyNames[companyCode] || companyCode} filter`}
                        >
                            <FeatherIcon.XCircle className='icons_table ' />
                        </button>
                    </div>
                ))}

                {/* Industry News Chips (grouped) */}
                {renderIndustryNewsChips()}

                {/* Customer Industry Chips (grouped) */}
                {renderCustomerIndustryChips()}

                {/* News Type Chips */}
                {selectedNewsTypes.map(newsType => (
                    <div key={`newstype-${newsType}`} className="filter-chip-news filter-chip-newstype-news">
                        <span className="chip-label-news">News Type:</span>
                        <span className="chip-value-news">{newsTypesMap[newsType] || newsType}</span>
                        <button
                            className="chip-remove-news"
                            onClick={() => onRemoveFilter('newsType', newsType)}
                            aria-label={`Remove ${newsTypesMap[newsType] || newsType} filter`}
                        >
                            <FeatherIcon.XCircle className='icons_table ' />
                        </button>
                    </div>
                ))}

                {/* Region Chips */}
                {selectedRegions.map(region => (
                    <div key={`region-${region}`} className="filter-chip-news filter-chip-region-news">
                        <span className="chip-label-news">Region:</span>
                        <span className="chip-value-news">{regionsMap[region] || region}</span>
                        <button
                            className="chip-remove-news"
                            onClick={() => onRemoveFilter('region', region)}
                            aria-label={`Remove ${regionsMap[region] || region} filter`}
                        >
                            <FeatherIcon.XCircle className='icons_table ' />
                        </button>
                    </div>
                ))}

                {/* Timeline Chip */}
                {onlyWithTimeline && (
                    <div className="filter-chip-news filter-chip-newstype-news">
                        <span className="chip-label-news">Highlight:</span>
                        <span className="chip-value-news">News with Timelines</span>
                        <button
                            className="chip-remove-news"
                            onClick={() => onRemoveFilter('timeline')}
                            aria-label="Remove timeline filter"
                        >
                            <FeatherIcon.XCircle className='icons_table ' />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ActiveFiltersBar;
